<script setup lang="ts">
import { ref, watch } from 'vue'
import { call } from '../api/http'
import { importStatement, ledger, type Account, type Import } from '../api/ledger'
import { useSession } from '../stores/session'
import { useForm } from '../lib/form'
import UiCard from '../components/UiCard.vue'
import UiButton from '../components/UiButton.vue'
import UiField from '../components/UiField.vue'
import ErrorBanner from '../components/ErrorBanner.vue'
import EmptyState from '../components/EmptyState.vue'

const session = useSession()
const load = useForm()
const upload = useForm()

const accounts = ref<Account[]>([])
const accountId = ref('')
const file = ref<File | null>(null)
const dragging = ref(false)
const result = ref<Import | null>(null)

watch(
  () => session.workspace?.id,
  () =>
    load.submit(async () => {
      accounts.value = (await call(ledger.GET('/accounts'))).filter((a) => a.is_active)
      accountId.value = accounts.value[0]?.id ?? ''
    }),
  { immediate: true },
)

function pick(f: File | undefined | null) {
  if (!f) return
  file.value = f
  result.value = null
}

function onDrop(e: DragEvent) {
  dragging.value = false
  pick(e.dataTransfer?.files[0])
}

async function onImport() {
  if (!file.value || !accountId.value) return
  const f = file.value
  await upload.submit(async () => (result.value = await importStatement(f, accountId.value)))
}
</script>

<template>
  <div class="page">
    <header class="page-header">
      <div>
        <h1>{{ $t('import.title') }}</h1>
        <p class="muted">{{ $t('import.subtitle') }}</p>
      </div>
    </header>

    <ErrorBanner :error="load.error.value" />

    <UiCard v-if="!session.canWrite">
      <p class="muted">{{ $t('ledger.readOnly') }}</p>
    </UiCard>
    <UiCard v-else-if="!load.busy.value && !accounts.length">
      <EmptyState :title="$t('import.noAccounts')" :message="$t('import.noAccountsText')">
        <RouterLink to="/accounts">{{ $t('accounts.new') }}</RouterLink>
      </EmptyState>
    </UiCard>
    <UiCard v-else>
      <form class="stack" data-testid="import-form" @submit.prevent="onImport">
        <UiField :label="$t('transactions.account')">
          <select v-model="accountId" name="account_id" required>
            <option v-for="a in accounts" :key="a.id" :value="a.id">{{ a.name }} · {{ a.currency }}</option>
          </select>
        </UiField>
        <label
          :class="['drop', { over: dragging }]"
          @dragover.prevent="dragging = true"
          @dragleave="dragging = false"
          @drop.prevent="onDrop"
        >
          <input type="file" name="file" accept=".xml,.csv,.xlsx" class="sr-only" @change="pick(($event.target as HTMLInputElement).files?.[0])" />
          <strong>{{ file ? file.name : $t('import.drop') }}</strong>
          <span class="muted small">{{ $t('import.formats') }}</span>
        </label>
        <ErrorBanner :error="upload.error.value" />
        <div>
          <UiButton type="submit" :disabled="!file" :loading="upload.busy.value">{{ $t('import.action') }}</UiButton>
        </div>
      </form>
    </UiCard>

    <UiCard v-if="result" :title="$t('import.resultTitle')" data-testid="import-result">
      <dl class="summary">
        <dt>{{ $t('import.format') }}</dt>
        <dd>{{ $t(`import.formatName.${result.format}`) }}</dd>
        <dt>{{ $t('import.period') }}</dt>
        <dd>{{ result.period_from ? `${result.period_from} – ${result.period_to}` : '—' }}</dd>
        <dt>{{ $t('import.inserted') }}</dt>
        <dd data-testid="import-inserted">{{ result.inserted }}</dd>
        <dt>{{ $t('import.duplicates') }}</dt>
        <dd data-testid="import-duplicates">{{ result.duplicates }}</dd>
        <dt>{{ $t('import.skipped') }}</dt>
        <dd data-testid="import-skipped">{{ result.skipped }}</dd>
      </dl>
      <details v-if="result.errors.length" class="errors">
        <summary>{{ $t('import.rowErrors', { n: result.errors.length }) }}</summary>
        <ul>
          <li v-for="e in result.errors" :key="e.row" class="small">#{{ e.row }}: {{ e.message }}</li>
        </ul>
      </details>
      <RouterLink :to="{ path: '/transactions' }">{{ $t('import.viewTransactions') }}</RouterLink>
    </UiCard>
  </div>
</template>

<style scoped>
.drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 2rem 1rem;
  border: 2px dashed var(--border);
  border-radius: var(--radius);
  text-align: center;
  cursor: pointer;
}
.drop.over,
.drop:hover {
  border-color: var(--accent);
}
.drop:focus-within {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.summary {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 0.35rem 1.5rem;
  margin: 0 0 1rem;
}
.summary dd {
  margin: 0;
  font-weight: 500;
}
.errors {
  margin-bottom: 1rem;
}
</style>
